/**
 * Subuser permission catalogue. This list is the single source of truth: panel route guards,
 * daemon JWT scopes and the daemon's own checks (generated Go file) all derive from it.
 */
export const PERMISSIONS = {
  websocket: {
    description: 'Connect to the server websocket.',
    keys: { connect: 'Receive console output and live stats.' },
  },
  control: {
    description: 'Control the server power state and console.',
    keys: {
      console: 'Send commands to the server console.',
      start: 'Start the server.',
      stop: 'Stop or kill the server.',
      restart: 'Restart the server.',
    },
  },
  user: {
    description: 'Manage subusers of the server.',
    keys: {
      create: 'Create subusers.',
      read: 'View subusers and their permissions.',
      update: 'Modify subusers.',
      delete: 'Remove subusers.',
    },
  },
  file: {
    description: 'Manage server files.',
    keys: {
      create: 'Create and upload files and folders.',
      read: 'List directories and download files.',
      'read-content': 'View the contents of files.',
      update: 'Modify existing files and folders.',
      delete: 'Delete files and folders.',
      archive: 'Create and extract archives.',
      sftp: 'Connect over SFTP (still bound by the other file permissions).',
    },
  },
  backup: {
    description: 'Manage server backups.',
    keys: {
      create: 'Create backups.',
      read: 'List backups.',
      delete: 'Delete backups.',
      download: 'Download backups.',
      restore: 'Restore backups (overwrites server files).',
    },
  },
  allocation: {
    description: 'Manage network allocations.',
    keys: {
      read: 'View allocations.',
      create: 'Add allocations.',
      update: 'Change the primary allocation and notes.',
      delete: 'Remove allocations.',
    },
  },
  startup: {
    description: 'Manage startup parameters.',
    keys: {
      read: 'View startup variables.',
      update: 'Modify startup variables.',
      'docker-image': 'Change the docker image.',
    },
  },
  software: {
    description: 'Manage the server software and version (Software tab).',
    keys: {
      read: 'See the installed software and browse versions.',
      change: 'Switch the server software and version, and undo a switch.',
    },
  },
  database: {
    description: 'Manage server databases.',
    keys: {
      create: 'Create databases.',
      read: 'View databases.',
      update: 'Rotate database passwords.',
      delete: 'Delete databases.',
      view_password: 'View database passwords.',
    },
  },
  schedule: {
    description: 'Manage schedules.',
    keys: {
      create: 'Create schedules.',
      read: 'View schedules.',
      update: 'Modify schedules and tasks.',
      delete: 'Delete schedules.',
    },
  },
  settings: {
    description: 'Manage server settings.',
    keys: {
      rename: 'Rename the server.',
      reinstall: 'Reinstall the server.',
    },
  },
  activity: {
    description: 'View server activity.',
    keys: { read: 'View the activity log.' },
  },
  // Game pages. Each page checks its own permissions, not the file.* ones it happens to use
  // underneath, so a subuser can be given one page without the whole file manager.
  player: {
    description: 'Players page.',
    keys: {
      read: 'See who is online.',
      manage: 'Kick, ban, whitelist and op players.',
    },
  },
  plugin: {
    description: 'Plugins and mods (including Rust Oxide/Carbon plugins).',
    keys: {
      read: 'See installed plugins and mods and search for new ones.',
      install: 'Install plugins and mods (updating needs install and remove).',
      remove: 'Remove plugins and mods.',
    },
  },
  world: {
    description: 'Worlds page.',
    keys: {
      read: 'List worlds and their sizes.',
      switch: 'Change the active world.',
      download: 'Download a world as an archive.',
      upload: 'Upload a world archive.',
      delete: 'Delete or reset a world.',
    },
  },
  config: {
    description: 'Config editors.',
    keys: {
      read: 'View the config editors.',
      update: 'Change settings through the config editors and set up RCON.',
    },
  },
  wipe: {
    description: 'Wipes (Rust).',
    keys: { run: 'Wipe the map or everything.' },
  },
} as const;

/**
 * Before the page permissions existed, these pages were gated by the file/console permissions
 * they use underneath. Migration 0015 granted each new permission to subusers who held all of
 * the listed ones, so nobody gained or lost access; kept here as the record of that mapping.
 */
export const PAGE_PERMISSION_ORIGINS: Record<string, readonly Permission[]> = {
  'player.read': ['websocket.connect'],
  'player.manage': ['control.console'],
  'plugin.read': ['file.read'],
  'plugin.install': ['file.create'],
  'plugin.remove': ['file.delete'],
  'world.read': ['file.read'],
  'world.switch': ['file.update'],
  'world.download': ['file.archive', 'file.read-content'],
  'world.upload': ['file.create', 'file.archive'],
  'world.delete': ['file.delete'],
  'config.read': ['file.read-content'],
  'config.update': ['file.read-content', 'file.update'],
  'wipe.run': ['file.delete'],
};

type Catalogue = typeof PERMISSIONS;
export type Permission = {
  [G in keyof Catalogue]: `${G & string}.${keyof Catalogue[G]['keys'] & string}`;
}[keyof Catalogue];

export const ALL_PERMISSIONS: readonly Permission[] = Object.entries(PERMISSIONS).flatMap(
  ([group, def]) => Object.keys(def.keys).map((key) => `${group}.${key}` as Permission),
);

const permissionSet: ReadonlySet<string> = new Set(ALL_PERMISSIONS);

export function isPermission(value: string): value is Permission {
  return permissionSet.has(value);
}

/** Owners and admins hold the wildcard; subusers hold an explicit list. */
export function hasPermission(held: readonly string[], wanted: Permission): boolean {
  return held.includes('*') || held.includes(wanted);
}
